import type { ThemeConfig } from 'antd';
import { App, ConfigProvider, theme as antdTheme } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import kkKZ from 'antd/lib/locale/kk_KZ';
import ruRU from 'antd/lib/locale/ru_RU';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Languages } from '@/shared/constants';
import { getSystemTheme } from '@/shared/services/theme';
import type { TResolvedTheme } from '@/shared/services/theme';
import { useAppDispatch, useAppSelector } from '@/store';
import { setTheme } from '@/store/app.slice';

const THEME_TOKEN_NAMES = [
  'brand-primary',
  'brand-on-primary',
  'brand-text',
  'background',
  'surface',
  'text-primary',
  'text-secondary',
  'border-strong',
  'accent-blue',
  'status-success',
  'status-warning',
  'status-info',
  'status-error'
] as const;

type TThemeTokens = Record<(typeof THEME_TOKEN_NAMES)[number], string>;

/**
 * Значения цветовых токенов схемы из src/styles/theme.scss — единственного источника цветов.
 * Читаются со скрытого элемента с атрибутом data-theme, поэтому не зависят от того,
 * какая схема сейчас выставлена на <html>.
 */
const readThemeTokens = (mode: TResolvedTheme): TThemeTokens => {
  const probe = document.createElement('div');
  probe.setAttribute('data-theme', mode);
  probe.hidden = true;
  document.body.append(probe);
  const styles = getComputedStyle(probe);
  const tokens = Object.fromEntries(
    THEME_TOKEN_NAMES.map((name) => [
      name,
      styles.getPropertyValue(`--color-${name}`).trim()
    ])
  ) as TThemeTokens;
  probe.remove();
  return tokens;
};

const getAntdTheme = (mode: TResolvedTheme): ThemeConfig => {
  const tokens = readThemeTokens(mode);
  return {
    algorithm:
      mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: tokens['brand-primary'],
      colorLink: tokens['brand-text'],
      colorError: tokens['status-error'],
      colorSuccess: tokens['status-success'],
      colorWarning: tokens['status-warning'],
      colorInfo: tokens['status-info'],
      fontFamily: 'Manrope, -apple-system, system-ui, sans-serif',
      borderRadius: 12,
      colorText: tokens['text-primary'],
      colorTextSecondary: tokens['text-secondary'],
      colorBgBase: tokens.background,
      colorBgContainer: tokens.surface,
      // Модалки и выпадашки — на фоне экрана, чтобы поля (colorBgContainer) на них читались.
      colorBgElevated: tokens.background,
      colorBorder: tokens['border-strong']
    },
    components: {
      // Текст на оранжевом colorPrimary — тёмный, как в макете (danger-кнопки не трогает).
      Button: {
        primaryColor: tokens['brand-on-primary']
      },
      Steps: {
        colorPrimary: tokens['accent-blue'],
        colorPrimaryBg: 'rgba(23, 111, 189, 0.12)' // accent-blue с прозрачностью 12%
      }
    }
  };
};

interface IProps {
  children: React.ReactNode;
}

const ThemeProvider: React.FC<IProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useAppDispatch();
  const { theme, themeMode } = useAppSelector((state) => state.app);

  // Включаем CSS-переменные тёмной схемы ([data-theme='dark']) из src/styles/theme.scss.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Снимаем инлайн-фон от анти-моргального скрипта (public/index.html) — дальше фон ведёт CSS.
    document.documentElement.style.backgroundColor = '';
  }, [theme]);

  // Схема оболочки живёт, пока смонтирована оболочка: при SPA-переходе в админку
  // (отдельная ветка роутера со своей темой) снимаем атрибут.
  useEffect(
    () => () => document.documentElement.removeAttribute('data-theme'),
    []
  );

  // В режиме system следим за системной темой и обновляем эффективную схему.
  useEffect(() => {
    if (themeMode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => dispatch(setTheme(getSystemTheme()));
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [themeMode, dispatch]);

  // Ссылка важна: новый объект темы на каждый рендер пересобирает стили antd, а чтение
  // токенов трогает DOM — считаем только при смене схемы.
  const antdThemeConfig = useMemo(() => getAntdTheme(theme), [theme]);

  const locale = useMemo(() => {
    switch (currentLang) {
      case Languages.ru:
        return ruRU;
      case Languages.en:
        return enUS;
      case Languages.kk:
        return kkKZ;
      default:
        return ruRU;
    }
  }, [currentLang]);

  return (
    <ConfigProvider locale={locale} theme={antdThemeConfig}>
      <App component={false}>{children}</App>
    </ConfigProvider>
  );
};

export default ThemeProvider;
